// This CustomEntityUserFilter class is designed to filter entities based on user-specific criteria.
// The getCatalogFilters method constructs filters, ensuring that entities are filtered by their full entity
// reference (kind, namespace, and name).
// This change is necessary to avoid issues with entities having the same name but different namespaces or kinds
// and to ensure that the filtering mechanism works correctly with Backstage's catalog-backend request format.
// It is based on Backstage opensource EntityFilter implementation found in from plugins/catalog-react/src/filters.ts

import {
  EntityFilter,
  getEntityRelations,
  UserListFilterKind,
} from '@backstage/plugin-catalog-react';

import {
  Entity,
  parseEntityRef,
  RELATION_OWNED_BY,
  stringifyEntityRef,
} from '@backstage/catalog-model';

export class CustomEntityUserFilter implements EntityFilter {
  private constructor(
    readonly value: UserListFilterKind,
    readonly refs?: string[],
  ) {}

  static owned(ownershipEntityRefs: string[]) {
    return new CustomEntityUserFilter('owned', ownershipEntityRefs);
  }

  static all() {
    return new CustomEntityUserFilter('all');
  }

  static starred(starredEntityRefs: string[]) {
    return new CustomEntityUserFilter('starred', starredEntityRefs);
  }

  getCatalogFilters(): Record<string, string[]> {
    if (this.value === 'owned') {
      return { 'relations.ownedBy': this.refs ?? [] };
    }

    if (this.value === 'starred') {
      if (!this.refs || this.refs.length === 0) {
        return {};
      }

      const kinds: string[] = [];
      const namespaces: string[] = [];
      const names: string[] = [];

      this.refs.forEach(ref => {
        const { kind, namespace, name } = parseEntityRef(ref);
        kinds.push(kind);
        namespaces.push(namespace);
        names.push(name);
      });

      return {
        kind: kinds,
        'metadata.namespace': namespaces,
        'metadata.name': names,
      };
    }

    return {};
  }

  filterEntity(entity: Entity) {
    if (this.value === 'starred') {
      return this.refs?.includes(stringifyEntityRef(entity)) ?? true;
    }
    // used only for retro-compatibility with non paginated data.
    // This is supposed to return always true for paginated
    // owned entities, since the filters are applied server side.
    if (this.value === 'owned') {
      const relations = getEntityRelations(entity, RELATION_OWNED_BY);

      return (
        this.refs?.some(v =>
          relations.some(o => stringifyEntityRef(o) === v),
        ) ?? false
      );
    }
    return true;
  }

  toQueryValue(): string {
    return this.value;
  }
}
